import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import PieChart from 'components/charts/PieChart';
import BarChart from 'components/charts/BarChart';
import LineChart from 'components/charts/LineChart';

const YandexMetrics = ({ siteId, ymetricaId, dateFrom, dateTo, currentLang }) => {
  const { token, API_URL, getCsrfToken } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [yandexToken, setYandexToken] = useState(null);
  
  // Состояние для данных метрик
  const [searchEngines, setSearchEngines] = useState({ series: [], options: {} });
  const [countries, setCountries] = useState({ series: [], options: {} });
  const [devices, setDevices] = useState({ series: [], options: {} });
  const [bounceRate, setBounceRate] = useState({ chartData: [], chartOptions: {} });
  const [visitsData, setVisitsData] = useState({ chartData: [], chartOptions: {} });
  const [advertisingData, setAdvertisingData] = useState({ chartData: [], chartOptions: {} });

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const setQuickPeriod = (period) => {
    const endDate = new Date();
    const startDate = new Date();
    
    switch (period) {
      case 'today':
        startDate.setDate(endDate.getDate());
        break;
      case 'yesterday':
        startDate.setDate(endDate.getDate() - 1);
        endDate.setDate(endDate.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(endDate.getDate() - 6);
        break;
      case 'thisMonth':
        startDate.setDate(1);
        break;
      case 'lastMonth':
        startDate.setMonth(startDate.getMonth() - 1, 1);
        endDate.setDate(0);
        break;
      default:
        startDate.setDate(endDate.getDate() - 6);
    }
    
    // Используем переданные даты из отчета
    return {
      startDate: dateFrom,
      endDate: dateTo
    };
  };

  const fetchYandexToken = async () => {
    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const tokenResponse = await fetch(`${API_URL}${currentLang}/yandex-metrica/token?site_id=${siteId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const tokenData = await tokenResponse.json();
      
      if (!tokenResponse.ok || !tokenData.success) {
        throw new Error(currentLang === 'ru' ? 'Не удалось получить токен доступа к Яндекс.Метрике' : 'Failed to get Yandex Metrica access token');
      }

      return tokenData.token;
    } catch (error) {
      throw error;
    }
  };

  const fetchMetricsData = async (token, endpoint, params) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `https://api-metrika.yandex.net/stat/v1/data?${queryString}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `OAuth ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Ошибка получения данных: ${response.status}`);
    }

    return await response.json();
  };

  const loadAllMetrics = async () => {
    if (!ymetricaId || ymetricaId === '0' || String(ymetricaId).trim() === '') {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await fetchYandexToken();
      setYandexToken(token);

      // Используем даты из отчета
      const date1 = dateFrom;
      const date2 = dateTo;

      // 1. Поисковые системы
      const searchEnginesData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:visits',
        dimensions: 'ym:s:searchEngine',
        sort: '-ym:s:visits',
        limit: 10
      });

      if (searchEnginesData.data && searchEnginesData.data.length > 0) {
        const searchSeries = searchEnginesData.data.map(item => parseInt(item.metrics[0]));
        const searchLabels = searchEnginesData.data.map(item => {
          const engine = item.dimensions[0].name;
          return currentLang === 'ru' ? 
            (engine === 'yandex' ? 'Яндекс' : 
             engine === 'google' ? 'Google' : 
             engine === 'bing' ? 'Bing' : 
             engine === 'mail.ru' ? 'Mail.ru' : 
             engine === 'rambler' ? 'Rambler' : engine) : engine;
        });

        setSearchEngines({
          series: searchSeries,
          options: {
            labels: searchLabels,
            colors: ['#FF0000', '#4285F4', '#00A1F1', '#005FF9', '#FF6600', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#EC4899'],
            legend: {
              position: 'bottom',
              fontSize: '12px'
            },
            tooltip: {
              y: {
                formatter: function(value) {
                  return value + (currentLang === 'ru' ? ' визитов' : ' visits');
                }
              }
            }
          }
        });
      }

      // 2. Страны
      const countriesData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:users',
        dimensions: 'ym:s:regionCountry',
        sort: '-ym:s:users',
        limit: 8
      });

      if (countriesData.data && countriesData.data.length > 0) {
        const countriesSeries = countriesData.data.map(item => parseInt(item.metrics[0]));
        const countriesLabels = countriesData.data.map(item => item.dimensions[0].name);

        setCountries({
          series: countriesSeries,
          options: {
            labels: countriesLabels,
            colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'],
            legend: {
              position: 'bottom',
              fontSize: '12px'
            },
            tooltip: {
              y: {
                formatter: function(value) {
                  return value + (currentLang === 'ru' ? ' пользователей' : ' users');
                }
              }
            }
          }
        });
      }

      // 3. Устройства
      const devicesData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:visits',
        dimensions: 'ym:s:deviceCategory',
        sort: '-ym:s:visits'
      });

      if (devicesData.data && devicesData.data.length > 0) {
        const devicesSeries = devicesData.data.map(item => parseInt(item.metrics[0]));
        const devicesLabels = devicesData.data.map(item => {
          const device = item.dimensions[0].name;
          return currentLang === 'ru' ? 
            (device === 'desktop' ? 'Компьютер' : 
             device === 'mobile' ? 'Мобильный' : 
             device === 'tablet' ? 'Планшет' : device) : device;
        });

        setDevices({
          series: devicesSeries,
          options: {
            labels: devicesLabels,
            colors: ['#3B82F6', '#10B981', '#F59E0B'],
            legend: {
              position: 'bottom',
              fontSize: '12px'
            },
            tooltip: {
              y: {
                formatter: function(value) {
                  return value + (currentLang === 'ru' ? ' визитов' : ' visits');
                }
              }
            }
          }
        });
      }

      // 4. Динамика отказов
      const bounceData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:bounceRate',
        dimensions: 'ym:s:date',
        sort: 'ym:s:date'
      });

      if (bounceData.data && bounceData.data.length > 0) {
        const categories = [];
        const seriesData = [];
        
        bounceData.data.forEach(item => {
          const date = new Date(item.dimensions[0].name);
          categories.push(date.toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US', {
            day: '2-digit',
            month: '2-digit'
          }));
          seriesData.push(parseFloat(item.metrics[0]).toFixed(1));
        });

        setBounceRate({
          chartData: [{
            name: currentLang === 'ru' ? 'Процент отказов' : 'Bounce Rate',
            data: seriesData,
            color: "#EF4444",
          }],
          chartOptions: {
            legend: {
              show: false,
            },
            theme: {
              mode: "light",
            },
            chart: {
              type: "line",
              toolbar: {
                show: false,
              },
            },
            dataLabels: {
              enabled: false,
            },
            stroke: {
              curve: 'smooth',
              width: 3,
            },
            tooltip: {
              style: {
                fontSize: "12px",
                fontFamily: undefined,
                backgroundColor: "#000000"
              },
              theme: 'dark',
              y: {
                formatter: function(value) {
                  return value + '%';
                }
              }
            },
            grid: {
              show: true,
              borderColor: '#E2E8F0',
              strokeDashArray: 3,
            },
            xaxis: {
              axisBorder: {
                show: false,
              },
              axisTicks: {
                show: false,
              },
              labels: {
                style: {
                  colors: "#A3AED0",
                  fontSize: "10px",
                  fontWeight: "500",
                },
                rotate: -45,
                rotateAlways: false,
              },
              type: "text",
              categories: categories,
            },
            yaxis: {
              show: true,
              labels: {
                style: {
                  colors: "#A3AED0",
                  fontSize: "10px",
                  fontWeight: "500",
                },
                formatter: function(value) {
                  return value + '%';
                }
              }
            },
            colors: ["#EF4444"],
            fill: {
              type: "gradient",
              gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.9,
                opacityTo: 0.9,
                stops: [0, 90, 100]
              }
            }
          }
        });
      }

      // 5. Динамика поисковых посетителей
      const visitorsData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:users',
        dimensions: 'ym:s:date',
        filters: 'ym:s:trafficSource==\'organic\'',
        sort: 'ym:s:date'
      });

      if (visitorsData.data && visitorsData.data.length > 0) {
        const categories = [];
        const seriesData = [];
        
        visitorsData.data.forEach(item => {
          const date = new Date(item.dimensions[0].name);
          categories.push(date.toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US', {
            day: '2-digit',
            month: '2-digit'
          }));
          seriesData.push(parseInt(item.metrics[0]));
        });

        setVisitsData({
          chartData: [{
            name: currentLang === 'ru' ? 'Поисковые посетители' : 'Search Visitors',
            data: seriesData,
          }],
          chartOptions: {
            legend: {
              show: false,
            },
            theme: {
              mode: "light",
            },
            chart: {
              type: "bar",
              toolbar: {
                show: false,
              },
            },
            dataLabels: {
              enabled: false,
            },
            plotOptions: {
              bar: {
                borderRadius: 4,
                columnWidth: '60%',
                distributed: false,
              },
            },
            tooltip: {
              style: {
                fontSize: "12px",
                fontFamily: undefined,
                backgroundColor: "#000000"
              },
              theme: 'dark',
              y: {
                formatter: function(value) {
                  return value + (currentLang === 'ru' ? ' поисковых посетителей' : ' search visitors');
                }
              }
            },
            grid: {
              show: true,
              borderColor: '#E2E8F0',
              strokeDashArray: 3,
            },
            xaxis: {
              axisBorder: {
                show: false,
              },
              axisTicks: {
                show: false,
              },
              labels: {
                style: {
                  colors: "#A3AED0",
                  fontSize: "10px",
                  fontWeight: "500",
                },
                rotate: -45,
                rotateAlways: false,
              },
              type: "text",
              categories: categories,
            },
            yaxis: {
              show: true,
              labels: {
                style: {
                  colors: "#A3AED0",
                  fontSize: "10px",
                  fontWeight: "500",
                }
              }
            },
            colors: ["#059669"],
            fill: {
              type: "solid",
              color: "#059669",
              opacity: 0.9
            }
          }
        });
      }

      // 6. Динамика рекламных посетителей
      const advertisingVisitorsData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:users',
        dimensions: 'ym:s:date',
        filters: 'ym:s:trafficSource!=\'organic\'',
        sort: 'ym:s:date'
      });

      if (advertisingVisitorsData.data && advertisingVisitorsData.data.length > 0) {
        const categories = [];
        const seriesData = [];
        
        advertisingVisitorsData.data.forEach(item => {
          const date = new Date(item.dimensions[0].name);
          categories.push(date.toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US', {
            day: '2-digit',
            month: '2-digit'
          }));
          seriesData.push(parseInt(item.metrics[0]));
        });

        setAdvertisingData({
          chartData: [{
            name: currentLang === 'ru' ? 'Рекламные посетители' : 'Advertising Visitors',
            data: seriesData,
          }],
          chartOptions: {
            legend: {
              show: false,
            },
            theme: {
              mode: "light",
            },
            chart: {
              type: "bar",
              toolbar: {
                show: false,
              },
            },
            dataLabels: {
              enabled: false,
            },
            plotOptions: {
              bar: {
                borderRadius: 4,
                columnWidth: '60%',
                distributed: false,
              },
            },
            tooltip: {
              style: {
                fontSize: "12px",
                fontFamily: undefined,
                backgroundColor: "#000000"
              },
              theme: 'dark',
              y: {
                formatter: function(value) {
                  return value + (currentLang === 'ru' ? ' рекламных посетителей' : ' advertising visitors');
                }
              }
            },
            grid: {
              show: true,
              borderColor: '#E2E8F0',
              strokeDashArray: 3,
            },
            xaxis: {
              axisBorder: {
                show: false,
              },
              axisTicks: {
                show: false,
              },
              labels: {
                style: {
                  colors: "#A3AED0",
                  fontSize: "10px",
                  fontWeight: "500",
                },
                rotate: -45,
                rotateAlways: false,
              },
              type: "text",
              categories: categories,
            },
            yaxis: {
              show: true,
              labels: {
                style: {
                  colors: "#A3AED0",
                  fontSize: "10px",
                  fontWeight: "500",
                }
              }
            },
            colors: ["#F59E0B"],
            fill: {
              type: "solid",
              color: "#F59E0B",
              opacity: 0.9
            }
          }
        });
      }

    } catch (error) {
      
      setError(error.message || 'Ошибка сети при получении данных метрики');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (siteId && ymetricaId && ymetricaId !== '0' && String(ymetricaId).trim() !== '') {
      loadAllMetrics();
    }
  }, [siteId, ymetricaId, currentLang, dateFrom, dateTo]);

  if (!ymetricaId || ymetricaId === '0' || String(ymetricaId).trim() === '') {
    return null;
  }

  return (
    <div className="min-h-[297mm] bg-white p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Аналитика Яндекс.Метрики
        </h2>
        <p className="text-gray-600">
          Статистика посещаемости и поведения пользователей за период отчета
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="flex items-center space-x-2">
            <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></span>
            <span className="text-gray-600">Загрузка данных метрики...</span>
          </div>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Динамика поисковых посетителей */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4 text-center">
              Динамика поисковых посетителей (SEO)
            </h3>
            {visitsData.chartData.length > 0 ? (
              <div className="h-64">
                <BarChart
                  chartData={visitsData.chartData}
                  chartOptions={visitsData.chartOptions}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                Нет данных
              </div>
            )}
          </div>

          {/* Динамика рекламных посетителей */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4 text-center">
              Динамика посетителей (Реклама, Прямые, Ссылки)
            </h3>
            {advertisingData.chartData.length > 0 ? (
              <div className="h-64">
                <BarChart
                  chartData={advertisingData.chartData}
                  chartOptions={advertisingData.chartOptions}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                Нет данных
              </div>
            )}
          </div>

          {/* Графики в сетке */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Поисковые системы */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold mb-4 text-center">
                Поисковые системы
              </h3>
              {searchEngines.series.length > 0 ? (
                <div className="h-64">
                  <PieChart
                    series={searchEngines.series}
                    options={searchEngines.options}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Нет данных
                </div>
              )}
            </div>

            {/* Страны */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold mb-4 text-center">
                География посетителей
              </h3>
              {countries.series.length > 0 ? (
                <div className="h-64">
                  <PieChart
                    series={countries.series}
                    options={countries.options}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Нет данных
                </div>
              )}
            </div>

            {/* Устройства */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold mb-4 text-center">
                Устройства
              </h3>
              {devices.series.length > 0 ? (
                <div className="h-64">
                  <PieChart
                    series={devices.series}
                    options={devices.options}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Нет данных
                </div>
              )}
            </div>

            {/* Динамика отказов */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold mb-4 text-center">
                Динамика отказов
              </h3>
              {bounceRate.chartData.length > 0 ? (
                <div className="h-64">
                  <LineChart
                    series={bounceRate.chartData}
                    options={bounceRate.chartOptions}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Нет данных
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YandexMetrics; 